import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Suspense, useCallback, useMemo } from "react";
import { Grid, LinearProgress, TextField } from "@material-ui/core";
import { addRegisteredSectionLayout } from "../../sectionLayoutDescriptions";
import { useResource } from "@rest-hooks/core";
import { ConditionParamCategoryOptionListResource, ConditionParamCategoryResource, DynamicFieldCategoryOptionListResource, DynamicFieldCategoryResource } from "@ploy-lib/rest-resources";
import { v4 as uuid } from "uuid";
import { escapeFormikName, filterAndStripEntriesStartingWith, unescapeFormikName } from "./utils";
import { CalcRulesDataProvider } from "./TableDataProvider";
import { secondValueIsNotNull } from "../../../utils";
import { FormTable } from "./FormTable";
import { FieldType } from "./tableSchema";
addRegisteredSectionLayout({
    name: "DecisionTableSection",
    displayName: "DecisionTableSection",
    settings: {
        editableOptions: {
            decisionTableType: true,
            fullWidth: true
        }
    }
});
const emptyArray = [];
const NumericOrFormat = "numericor";
const NumericOrEscapeFormat = "numericorescape";
const DateFormat = "date";
const emptyDecisionTable = () => ({
    decisionTableTemplateId: uuid(),
    type: "",
    rows: []
});
function sortDecisionTableColumns(columns, mappedColumns) {
    return [...columns].sort((a, b) => (mappedColumns[a.name]?.sortOrder ?? 0) -
        (mappedColumns[b.name]?.sortOrder ?? 0));
}
function makeDecisionTableTemplateValue(name, value) {
    return {
        decisionTableTemplateValueId: uuid(),
        category: name,
        value
    };
}
//
// Functions for converting raw values between what we save vs what the table operate on (eg. conversion of string to list)
//
const conditionText = "condition";
const dynamicFieldText = "dynamicField";
const convertStorageValueToRowCell = (value, category) => {
    if (category?.formatOptions?.toLowerCase() === NumericOrFormat) {
        return value
            .split("|")
            .filter(v => v)
            .map(v => v.substring(1, v.length - 1));
    }
    if (category?.formatOptions?.toLowerCase() === NumericOrEscapeFormat) {
        return value
            .split("|")
            .filter(v => v)
            .map(v => v.substring(1, v.length - 1).replace(/\\§/g, "|"));
    }
    return value;
};
function convertRowCellValueToStorageValue(name, value, category) {
    let stringValue = !Array.isArray(value) ? value : "";
    if (Array.isArray(value) &&
        category?.formatOptions?.toLowerCase() === NumericOrFormat) {
        stringValue = value
            .filter(v => v)
            .map(v => `^${v}$`)
            .join("|");
    }
    if (Array.isArray(value) &&
        category?.formatOptions?.toLowerCase() === NumericOrEscapeFormat) {
        stringValue = value
            .filter(v => v)
            .map(v => `^${v.replace(/\|/g, "\\§")}$`)
            .join("|");
    }
    return makeDecisionTableTemplateValue(name, stringValue);
}
//
// Conversion between lists of DecisionTableTemplateValue and row cells according
// condition categories or dynamic field categaries
//
function convertRowCellsAccordingToCategory(prefix, rowData, categories) {
    return filterAndStripEntriesStartingWith(`${prefix}:`, Object.entries(rowData))
        .filter(secondValueIsNotNull)
        .map(([name, value]) => [unescapeFormikName(name), value])
        .map(([name, value]) => convertRowCellValueToStorageValue(name, value, categories[name]));
}
function makeRowCellsAccordingToCategory(prefix, values, categories) {
    return values.map(c => [
        `${prefix}:${escapeFormikName(c.category)}`,
        convertStorageValueToRowCell(c.value, categories[c.category])
    ]);
}
//
// Factories for constructing functions that converts a DecisionTableTemplateRow into row cells and back again
//
const convertRowDataToStorageDataFactory = (conditionCategories, dynamicFieldCategories) => (rowData) => ({
    decisionTableTemplateRowId: uuid(),
    conditions: convertRowCellsAccordingToCategory(conditionText, rowData, conditionCategories),
    dynamicFields: convertRowCellsAccordingToCategory(dynamicFieldText, rowData, dynamicFieldCategories)
});
const convertStorageDataToRowDataFactory = (conditionCategories, dynamicFieldCategories) => (decisionTemplateRow) => Object.fromEntries([
    ...makeRowCellsAccordingToCategory(conditionText, decisionTemplateRow.conditions ?? [], conditionCategories),
    ...makeRowCellsAccordingToCategory(dynamicFieldText, decisionTemplateRow.dynamicFields ?? [], dynamicFieldCategories)
]);
//
function filterAndSortTemplateColumns(prefix, tableColumns, mappedColumns) {
    let columns = tableColumns.filter(tc => tc.tableType === prefix);
    columns = sortDecisionTableColumns(columns, mappedColumns);
    return columns;
}
const DecisionTableSectionInternal = (props) => {
    const { decisionTableType, tableColumns = emptyArray, allowRuntimeColumnAdditions, literal } = props;
    //
    // Retrieve necessary data (column definitions and option value lists) for displaying the decision table correctly
    //
    const tableTypeParams = decisionTableType
        ? {
            tableType: decisionTableType
        }
        : null;
    const [availableConditionColumns = emptyArray, availableDynamicFieldColumns = emptyArray] = useResource([ConditionParamCategoryResource.list(), tableTypeParams], [DynamicFieldCategoryResource.list(), tableTypeParams]);
    const [mappedConditionColumns, idMappedConditionColumns] = useMemo(() => [
        Object.fromEntries(availableConditionColumns.map(c => [c.name, c])),
        Object.fromEntries(availableConditionColumns.map(c => [c.conditionParamCategoryId, c]))
    ], [availableConditionColumns]);
    const [mappedDynamicFieldColumns, idMappedDynamicFieldColumns] = useMemo(() => [
        Object.fromEntries(availableDynamicFieldColumns.map(c => [c.name, c])),
        Object.fromEntries(availableDynamicFieldColumns.map(c => [c.dynamicFieldCategoryId, c]))
    ], [availableDynamicFieldColumns]);
    const conditionColumns = useMemo(() => filterAndSortTemplateColumns(conditionText, tableColumns, mappedConditionColumns), [mappedConditionColumns, tableColumns]);
    const dynamicFieldColumns = useMemo(() => filterAndSortTemplateColumns(dynamicFieldText, tableColumns, mappedDynamicFieldColumns), [mappedDynamicFieldColumns, tableColumns]);
    const conditionOptionColumns = conditionColumns
        .filter(c => c.optionSource == null && mappedConditionColumns[c.name].optionSource)
        .map(c => mappedConditionColumns[c.name].conditionParamCategoryId);
    const dynamicFieldOptionColumns = dynamicFieldColumns
        .filter(c => c.optionSource == null && mappedDynamicFieldColumns[c.name].optionSource)
        .map(c => mappedDynamicFieldColumns[c.name].dynamicFieldCategoryId);
    const [conditionOptionValues = emptyArray, dynamicFieldOptionValues = emptyArray] = useResource([
        ConditionParamCategoryOptionListResource.list(),
        conditionOptionColumns.length > 0
            ? {
                ids: conditionOptionColumns
            }
            : null
    ], [
        DynamicFieldCategoryOptionListResource.list(),
        dynamicFieldOptionColumns.length > 0
            ? {
                ids: dynamicFieldOptionColumns
            }
            : null
    ]);
    const mappedConditionOptionValues = useMemo(() => {
        return {
            ...Object.fromEntries(conditionOptionValues.map(c => [
                idMappedConditionColumns[c.id]?.name ?? "",
                c.values
            ]))
        };
    }, [conditionOptionValues, idMappedConditionColumns]);
    const mappedDynamicFieldOptionValues = useMemo(() => ({
        ...Object.fromEntries(dynamicFieldOptionValues.map(c => [
            idMappedDynamicFieldColumns[c.id]?.name ?? "",
            c.values
        ]))
    }), [dynamicFieldOptionValues, idMappedDynamicFieldColumns]);
    //
    // Set up value converters
    //
    const convertRawStorageToStorageDataRows = useCallback((decisionTableTemplates) => {
        if (!Array.isArray(decisionTableTemplates))
            return [];
        return (decisionTableTemplates.find(dtt => dtt.type === decisionTableType)
            ?.rows ?? []);
    }, [decisionTableType]);
    const convertStorageDataToRowCells = useMemo(() => convertStorageDataToRowDataFactory(mappedConditionColumns, mappedDynamicFieldColumns), [mappedConditionColumns, mappedDynamicFieldColumns]);
    const convertRowCellsToStorageData = useMemo(() => convertRowDataToStorageDataFactory(mappedConditionColumns, mappedDynamicFieldColumns), [mappedConditionColumns, mappedDynamicFieldColumns]);
    const convertStorageDataRowsToRawStorage = useCallback((storageRows, currentRawStorage) => {
        const tables = Array.isArray(currentRawStorage)
            ? currentRawStorage
            : [];
        const existingTable = tables.find(dtt => dtt.type === decisionTableType);
        const updatedTable = {
            ...emptyDecisionTable(),
            ...existingTable,
            type: decisionTableType ?? "",
            rows: storageRows
        };
        // Replace or add the updated table
        return existingTable
            ? tables.map(dtt => dtt.type === decisionTableType ? updatedTable : dtt)
            : [...tables, updatedTable];
    }, [decisionTableType]);
    //
    // Set up column definitions
    //
    const [columns, extraMetadata] = useMemo(() => {
        // Captures some state (template columns and decision table defitinition) and returns a function
        // that will patch a template table column with relevant name, description, option values and so forth.
        const columnPatcherMaker = (prefix, dtColumns, dtColumnOptionValues) => (column) => {
            const dtColumn = dtColumns[column.name];
            const patchedColumn = { ...column };
            patchedColumn.name = `${prefix}:${escapeFormikName(column.name)}`;
            patchedColumn.label =
                column.label ?? dtColumn.description ?? column.name;
            patchedColumn.optionValues = patchedColumn.optionValues
                ? patchedColumn.optionValues
                : patchedColumn.optionSource
                    ? undefined
                    : dtColumnOptionValues[column.name];
            patchedColumn.isMultipleSelect =
                dtColumn?.formatOptions?.toLowerCase() === NumericOrFormat ||
                    dtColumn?.formatOptions?.toLowerCase() === NumericOrEscapeFormat;
            patchedColumn.editable = !literal;
            const columnMetadata = {
                fieldType: patchedColumn.isMultipleSelect
                    ? FieldType.Multiselect
                    : patchedColumn.optionSource != null ||
                        patchedColumn.optionValues != null
                        ? FieldType.Select
                        : dtColumn.formatOptions?.toLowerCase() === DateFormat
                            ? FieldType.Date
                            : dtColumn.isNumeric
                                ? FieldType.Number
                                : FieldType.Text
            };
            return { patchedColumn, columnMetadata };
        };
        const mappedTableData = {
            conditions: conditionColumns.map(columnPatcherMaker(conditionText, mappedConditionColumns, mappedConditionOptionValues)),
            dynamicFields: dynamicFieldColumns.map(columnPatcherMaker(dynamicFieldText, mappedDynamicFieldColumns, mappedDynamicFieldOptionValues))
        };
        // Actual table columns used in FormTable
        const tableColumns = [
            ...mappedTableData.conditions.map(cond => cond.patchedColumn),
            ...(mappedTableData.conditions.length > 0 &&
                mappedTableData.dynamicFields.length > 0
                ? [
                    {
                        name: "divider",
                        formTemplateTableColumnId: "",
                        tableType: decisionTableType || ""
                    }
                ]
                : []),
            ...mappedTableData.dynamicFields.map(dynField => dynField.patchedColumn)
        ];
        // Setup custom metadata, mostly to create relevant display type
        const metadata = Object.fromEntries([
            ...mappedTableData.conditions.map(columnData => [columnData.patchedColumn.name, columnData.columnMetadata]),
            ...mappedTableData.dynamicFields.map(columnData => [columnData.patchedColumn.name, columnData.columnMetadata])
        ]);
        return [tableColumns, metadata];
    }, [
        conditionColumns,
        decisionTableType,
        dynamicFieldColumns,
        literal,
        mappedConditionColumns,
        mappedConditionOptionValues,
        mappedDynamicFieldColumns,
        mappedDynamicFieldOptionValues
    ]);
    //
    // We are ready to render
    //
    if (!decisionTableType)
        return null;
    return (_jsxs("div", { className: props.className, onClick: props.onClick, children: [allowRuntimeColumnAdditions && (_jsxs(Grid, { container: true, children: [_jsx(Grid, { item: true, xs: 6, children: _jsx(TextField, { variant: "standard" }) }), _jsx(Grid, { item: true, xs: 6, children: _jsx(TextField, { variant: "standard" }) })] })), _jsx(CalcRulesDataProvider, { tableType: "DecisionTableTemplates", namespace: "Calculator", name: "Calculator.DecisionTableTemplates", tableColumns: columns, convertRawStorageToStorageDataRows: convertRawStorageToStorageDataRows, convertStorageDataToRowCells: convertStorageDataToRowCells, convertRowCellsToStorageData: convertRowCellsToStorageData, convertStorageDataRowsToRawStorage: convertStorageDataRowsToRawStorage, children: providerProps => (_jsx(FormTable, { literal: literal, ...props, ...providerProps, tableSchema: "DTTableSchema", extendedMetadata: extraMetadata })) })] }));
};
const DecisionTableSection = (props) => {
    return (_jsx(Suspense, { fallback: _jsx(LinearProgress, { style: { marginBottom: 20 } }), children: _jsx(DecisionTableSectionInternal, { ...props }) }));
};
DecisionTableSection.displayName = "DployDecisionTableSection";
export { DecisionTableSection };
//# sourceMappingURL=DecisionTableSection.js.map